import React, {useEffect, useState} from 'react';

import {useCreateFolder} from 'app/folders/api';
import {
  createFormErrorsContext,
  ShowNonFieldFormErrors,
} from 'app/general/forms';
import {PopoverHeading} from 'app/general/overlays';
import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import {KBTreeItemInnerButton} from 'app/kbs/components/KBEditPage/KBTree/KBTreeStructures';
import {gt} from 'app/utils/i18n';

import {ChangeFolder, DoChangeProps} from './ChangeFolder';

import type {FormErrorsProps} from 'app/general/forms';
import type {
  BasicTeamSerializedType,
  FolderHashidSerializedType,
  FolderSerializedType,
} from 'sweet-types';

interface Props {
  onCreate: () => Promise<any>;
  onEditing?: (status: boolean) => Promise<any>;
  parent: FolderSerializedType | null;
  allowedTeams?: Array<BasicTeamSerializedType>;
  canEdit: boolean;
}

export const CreateFolderButton = (props: Props) => {
  const [editing, setEditing] = useState<boolean>(false);
  useEffect(() => {
    props.onEditing != null ? props.onEditing(editing) : null;
  }, [editing]);
  return props.canEdit ? (
    editing ? (
      <CreateFolderForm
        parent={props.parent}
        teams={props.allowedTeams || []}
        onCreate={() =>
          props.onCreate().then(
            () => setEditing(false),
            () => setEditing(false)
          )
        }
        onCancel={() => setEditing(false)}
      />
    ) : (
      <ItemWithChildrenStructure>
        <KBTreeItemInnerButton
          className="btn-link btn-sm"
          itemIcon={<i className="fas fa-plus-circle" />}
          onClick={() => setEditing(true)}
        >
          {gt('Add new Folder')}
        </KBTreeItemInnerButton>
      </ItemWithChildrenStructure>
    )
  ) : null;
};

export interface CreateFolderFormProps extends FormErrorsProps {
  onCreate: (folder: FolderSerializedType) => unknown;
  onCancel: () => void;
  parent: FolderHashidSerializedType | FolderSerializedType | null;
  teams?: Array<BasicTeamSerializedType>;
  autoFocus?: boolean;
  inPopover?: boolean;
}
export const CreateFolderForm = createFormErrorsContext<CreateFolderFormProps>(
  ({
    teams = [],
    parent,
    onCreate,
    onCancel,
    autoFocus = false,
    captureFormErrorsFromRejection,
    inPopover = false,
  }: CreateFolderFormProps) => {
    const [createFolder] = useCreateFolder();

    function doCreateFolder({name, teams}: DoChangeProps) {
      return createFolder({
        name,
        teamURLs: teams.map((team) => team.url),
        parent: parent ?? undefined,
      })
        .unwrap()
        .then(onCreate, captureFormErrorsFromRejection);
    }

    return (
      <>
        {inPopover ? (
          <PopoverHeading className="mb-2">
            {parent == null
              ? gt('Add folder')
              : gt('Add folder in "%s" folder', parent.name)}
          </PopoverHeading>
        ) : null}
        <ChangeFolder
          autoFocus={autoFocus}
          allowedTeams={teams}
          isSelectOnly={parent != null}
          doChange={doCreateFolder}
          onTeamChange={() => Promise.resolve(true)}
          onCancel={onCancel}
          initial={{
            name: '',
            teams,
          }}
          inPopover={inPopover}
        />
        <ShowNonFieldFormErrors />
      </>
    );
  }
);
